import React from 'react';
import { ConfigProvider, List, Typography } from '@duxton-web/ui';
import { Link } from 'react-router-dom';

import { useVariableContext } from '../../contexts/variable-context';
import Service from '@/lib/App';
import { FacetView, ScopeStatsView } from '@/lib/PlannerDTOs';

interface StatisticsProps {
  scopeStats: ScopeStatsView;
}

const SystemFacets: string[] = ['uuid', 'tsi', 'time', 'bsdk'];

const InternalFacets: string[] = [
  'dom',
  'exp',
  'sr',
  'vrt',
  'event',
  'val',
  'sval',
  'trt',
];

const StatisticsList = ({ facetsData }: { facetsData: any[] }) => {
  if (!facetsData?.length) return null;

  return (
    <List
      size="small"
      bordered
      dataSource={facetsData}
      renderItem={(facet) => (
        <List.Item key={facet.name}>
          <div style={{ width: '100%' }}>
            <div>{facet.name}</div>
          </div>
        </List.Item>
      )}
    />
  );
};

const InfoFacetsSection = ({ facetsData }: { facetsData: any[] }) => {
  return (
    <>
      <Typography.Title level={4}>Info Facets</Typography.Title>
      <StatisticsList facetsData={facetsData} />
    </>
  );
};

const UserFacetsSection = ({ facetsData }: { facetsData: any[] }) => {
  return (
    <>
      <Typography.Title level={4}>User Facets</Typography.Title>
      <StatisticsList facetsData={facetsData} />
    </>
  );
};

const CustomizedFacetsSection = ({ facetsData }: { facetsData: any[] }) => {
  return (
    <>
      <Typography.Title level={4}>User Customized Facets</Typography.Title>
      <StatisticsList facetsData={facetsData} />
    </>
  );
};

const Statistics = ({ scopeStats }: StatisticsProps) => {
  const { variable } = useVariableContext();

  const isUserFacet = (key: string) => {
    let filtered = Service.cache!.getAllFacets().filter((element) => {
      return key === element.name;
    });
    return filtered.length > 0;
  };

  const isInternalFacets = (key: string) => {
    return InternalFacets.includes(key);
  };

  const isSystemFacet = (key: string) => {
    return SystemFacets.includes(key);
  };

  const getTableData = (value: FacetView, key: string) => {
    let data: { value: string; count: number; link: string }[] = [];
    if (value.values) {
      let values = new Map(Object.entries(value.values));
      values.forEach((v: any, k: string) => {
        let l = '';
        switch (key) {
          case 'exp':
            if (k === 'rollout') {
              l = `/t/variables/${variable}/rollout`;
            } else {
              l = `/t/experiments/started/${k}/overview`;
            }
            break;
          case 'vrt':
            // vrt could comes from experiment
            l = `/t/variables/${variable}/rollout`;
            break;
        }

        data.push({
          value: k,
          count: v,
          link: l,
        });
      });
    }
    let facetMeta = Service.cache!.getFacet(key);
    let title = facetMeta ? `${facetMeta.title} (${key})` : key;
    let desc = facetMeta ? facetMeta.description : '';
    let columns = [
      {
        title: 'value',
        dataIndex: 'value',
        key: 'value',
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        render: (text, row) => <div>{text}</div>,
        sorter: (a, b) => a.value.localeCompare(b.value),
      },
      {
        title: 'Count(1hr)',
        dataIndex: 'count',
        key: 'count',
        sorter: (a, b) => a.count - b.count,
      },
    ];
    switch (key) {
      case 'exp':
        title = 'Resolved By (exp)';
        columns[0].render = (text, row) => <Link to={row.link}>{text}</Link>;
        break;
      case 'vrt':
        title = 'Variant ID (vrt)';
        desc =
          'Variant might come from rollout or experiment, manual matching is required';
        columns[0].render = (text, row) => <Link to={row.link}>{text}</Link>;
        break;
      case 'event':
        title = 'Event (event)';
        break;
      case 'val':
        title = 'Value (val)';
        break;
      case 'sval':
        title = 'String Value (sval)';
        break;
    }

    return {
      name: title,
      cardinality: value.cardinality,
      columns: columns,
      data: data,
      description: desc,
    };
  };

  let facets = new Map(Object.entries(scopeStats.facets));
  let userFacetsData: any[] = [];
  let customerFacetsData: any[] = [];
  let internalFacetsData: any[] = [];
  facets.forEach((value: FacetView, key: string) => {
    let tableData = getTableData(value, key);
    if (!isSystemFacet(key)) {
      if (isInternalFacets(key)) {
        internalFacetsData.push(tableData);
      } else if (isUserFacet(key)) {
        userFacetsData.push(tableData);
      } else {
        customerFacetsData.push(tableData);
      }
    }
  });

  return (
    <ConfigProvider
      theme={{
        components: {
          Typography: {
            titleMarginBottom: 0,
          },
        },
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <InfoFacetsSection facetsData={internalFacetsData} />
        <UserFacetsSection facetsData={userFacetsData} />
        <CustomizedFacetsSection facetsData={customerFacetsData} />
      </div>
    </ConfigProvider>
  );
};

export default Statistics;
